import React, { useRef, useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Stack,
  alpha,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material'
import { validateImageFile, createImagePreview } from '../../utils/imageUtils'
import { ImageCropper } from './ImageCropper'

// Parse aspect ratio string to number (e.g., "3:1" -> 3, "1:1" -> 1, "4:3" -> 1.333)
function parseAspectRatio(ratio?: string): number | undefined {
  if (!ratio) return undefined
  const parts = ratio.split(':')
  if (parts.length !== 2) return undefined
  const width = parseFloat(parts[0])
  const height = parseFloat(parts[1])
  if (isNaN(width) || isNaN(height) || height === 0) return undefined
  return width / height
}

export interface ImageUploadZoneProps {
  /** Current image URL (if any) */
  value?: string | null
  /** Callback when file is selected and validated */
  onFileSelect: (file: File) => void
  /** Callback when upload is cancelled/cleared */
  onClear?: () => void
  /** Maximum file size in MB */
  maxSizeMB?: number
  /** Label for the upload area */
  label?: string
  /** Description text */
  description?: string
  /** Whether upload is in progress */
  isUploading?: boolean
  /** Upload progress (0-100) */
  uploadProgress?: number
  /** Error message to display */
  error?: string
  /** Allow camera capture on mobile */
  allowCamera?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Aspect ratio hint (e.g., "1:1" for avatar, "3:1" for cover) */
  aspectRatio?: string
  /** Enable image cropping before upload */
  enableCropping?: boolean
  /** Camera capture mode: 'user' for front camera (selfies), 'environment' for back camera */
  cameraMode?: 'user' | 'environment'
  /** Callback when "Select from Files" button is clicked. If provided, shows the button. */
  onSelectFromFiles?: () => void
}

export function ImageUploadZone({
  value,
  onFileSelect,
  onClear,
  maxSizeMB = 5,
  label = 'Upload Image',
  description = 'Drag and drop, paste, or click to select',
  isUploading = false,
  uploadProgress = 0,
  error,
  allowCamera = true,
  disabled = false,
  aspectRatio,
  enableCropping = true,
  cameraMode = 'environment',
  onSelectFromFiles,
}: ImageUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<string | null>(value || null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [fileToCrop, setFileToCrop] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)

  // Update preview when value changes
  useEffect(() => {
    setPreview(value || null)
  }, [value])

  // Handle paste from clipboard
  useEffect(() => {
    if (disabled || isUploading) return

    const handlePaste = (e: ClipboardEvent) => {
      // Only handle paste if the drop zone or its children have focus
      if (!dropZoneRef.current?.contains(document.activeElement)) {
        return
      }

      const items = e.clipboardData?.items
      if (!items) return

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) {
            handleFile(file)
          }
          break
        }
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [disabled, isUploading])

  const handleFile = async (file: File) => {
    setValidationError(null)

    // Validate file
    const validation = validateImageFile(file, maxSizeMB)
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid file')
      return
    }

    // If cropping is enabled, show the cropper dialog
    // Cropping works with or without an aspectRatio (free-form when undefined)
    if (enableCropping) {
      setFileToCrop(file)
      setCropperOpen(true)
      return
    }

    // Otherwise, proceed directly
    try {
      const previewUrl = await createImagePreview(file)
      setPreview(previewUrl)
      onFileSelect(file)
    } catch (err) {
      setValidationError('Failed to read image file')
    }
  }

  const handleCropComplete = async (croppedFile: File) => {
    setCropperOpen(false)
    setFileToCrop(null)

    try {
      const previewUrl = await createImagePreview(croppedFile)
      setPreview(previewUrl)
      onFileSelect(croppedFile)
    } catch (err) {
      setValidationError('Failed to process cropped image')
    }
  }

  const handleCropperClose = () => {
    setCropperOpen(false)
    setFileToCrop(null)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set dragging to false if we're leaving the drop zone itself
    if (e.currentTarget === e.target) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled || isUploading) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleClear = () => {
    setPreview(null)
    setValidationError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
    onClear?.()
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleCameraClick = () => {
    cameraInputRef.current?.click()
  }

  const displayError = error || validationError

  return (
    <Box>
      <Paper
        ref={dropZoneRef}
        tabIndex={disabled || isUploading ? -1 : 0}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          border: '2px dashed',
          borderColor: displayError
            ? 'error.main'
            : isDragging
              ? 'primary.main'
              : 'divider',
          bgcolor: isDragging
            ? (theme) => alpha(theme.palette.primary.main, 0.05)
            : 'background.paper',
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled || isUploading ? 'divider' : 'primary.main',
            bgcolor: disabled || isUploading
              ? 'background.paper'
              : (theme) => alpha(theme.palette.primary.main, 0.02),
          },
          '&:focus': {
            outline: 'none',
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
        }}
        onClick={disabled || isUploading || preview ? undefined : handleBrowseClick}
      >
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled || isUploading}
          aria-label="Select image file"
        />
        {allowCamera && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture={cameraMode}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={disabled || isUploading}
            aria-label="Capture image with camera"
          />
        )}

        {preview ? (
          // Show preview
          <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 1,
                display: 'block',
                margin: '0 auto',
              }}
            />
            {!isUploading && !disabled && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                }}
                aria-label="Clear image"
              >
                <CloseIcon />
              </IconButton>
            )}
            {isUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#000', 0.5),
                  borderRadius: 1,
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress
                    variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                    value={uploadProgress}
                    sx={{ color: 'white' }}
                  />
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Uploading{uploadProgress > 0 ? ` ${uploadProgress}%` : '...'}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        ) : (
          // Show upload prompt
          <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
            {isUploading ? (
              <>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Uploading...
                </Typography>
              </>
            ) : (
              <>
                <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                <Typography variant="h6" align="center">
                  {label}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {description}
                </Typography>
                {aspectRatio && (
                  <Typography variant="caption" color="text.secondary">
                    Recommended aspect ratio: {aspectRatio}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleBrowseClick()
                    }}
                    disabled={disabled}
                  >
                    Local Files
                  </Button>
                  {allowCamera && (
                    <Button
                      variant="outlined"
                      startIcon={<CameraIcon />}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCameraClick()
                      }}
                      disabled={disabled}
                    >
                      Camera
                    </Button>
                  )}
                  {onSelectFromFiles && (
                    <Button
                      variant="outlined"
                      startIcon={<CollectionsIcon />}
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectFromFiles()
                      }}
                      disabled={disabled}
                    >
                      Server Files
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Max size: {maxSizeMB}MB • JPEG, PNG, WebP, GIF
                </Typography>
              </>
            )}
          </Stack>
        )}
      </Paper>

      {/* Error message */}
      {displayError && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
          role="alert"
          aria-live="polite"
        >
          {displayError}
        </Typography>
      )}

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={cropperOpen}
        imageFile={fileToCrop}
        onCropComplete={handleCropComplete}
        onClose={handleCropperClose}
        aspectRatio={parseAspectRatio(aspectRatio)}
        title={`Crop ${label}`}
      />
    </Box>
  )
}
